/**
 * AccountDialog Component
 * 
 * Dialog for creating and editing accounts with form fields for
 * group, name, amount, currency, description, and account details.
 */

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Account, Currency, AccountDetailValue } from "../../../types";
import { AccountGroupDoc } from "../hooks/useAccountGroups";
import DuplicateDetectionTextField from "../../../components/DuplicateDetectionTextField";
import InfoTooltipIcon from "../../../components/InfoTooltipIcon";
import CannotChangeLaterIcon from "../../../components/CannotChangeLaterIcon";

interface AccountDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  editingAccount: Account | null;
  formData: {
    group: string;
    accountName: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  };
  setFormData: React.Dispatch<React.SetStateAction<{
    accountName: string;
    group: string;
    amount: string;
    currency: string;
    description: string;
    details?: AccountDetailValue;
  }>>;
  showAccountDetails: boolean;
  setShowAccountDetails: React.Dispatch<React.SetStateAction<boolean>>;
  initialShowAccountDetails: boolean;
  setInitialShowAccountDetails: React.Dispatch<React.SetStateAction<boolean>>;
  accountGroups: AccountGroupDoc[];
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
  accountNameError: string;
  setAccountNameError: React.Dispatch<React.SetStateAction<string>>;
  isSavingAccount: boolean;
  creatingAccountName: string | null;
  setCreatingAccountName: React.Dispatch<React.SetStateAction<string | null>>;
  getSelectedGroupDetails: () => import("../../../types").AccountGroupDetailField[];
  handleDetailValueChange: (detailName: string, value: string | number) => void;
  hasFormChanged: () => boolean;
  isAccountFormValid: boolean;
}

/**
 * Account Dialog Component
 */
export default function AccountDialog({
  open,
  onClose,
  onSubmit,
  editingAccount,
  formData,
  setFormData,
  showAccountDetails,
  setShowAccountDetails,
  initialShowAccountDetails,
  setInitialShowAccountDetails,
  accountGroups,
  accounts,
  currencies,
  defaultCurrency,
  accountNameError,
  setAccountNameError,
  isSavingAccount,
  creatingAccountName,
  setCreatingAccountName,
  getSelectedGroupDetails,
  handleDetailValueChange,
  hasFormChanged,
  isAccountFormValid,
}: AccountDialogProps): React.JSX.Element {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">
            {editingAccount ? "Edit Account" : "Create Account"}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={onSubmit} id="account-form">
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Group</InputLabel>
              <Select
                value={formData.group}
                label="Group"
                onChange={(e) => {
                  const newGroup = e.target.value;
                  // Clear details and uncheck checkbox when group changes
                  setFormData({ ...formData, group: newGroup, details: {} });
                  setShowAccountDetails(false);
                  // Update initial state if editing to reflect the change
                  if (editingAccount) {
                    setInitialShowAccountDetails(false);
                  }
                }}
              >
                {accountGroups.map((group) => (
                  <MenuItem key={group.id} value={group.name}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DuplicateDetectionTextField
              required
              label="Account Name"
              value={formData.accountName}
              onChange={(value) => {
                setFormData({ ...formData, accountName: value });
              }}
              existingItems={accounts
                .filter((acc) => {
                  // When creating, exclude the account being created from duplicate check
                  if (isSavingAccount && creatingAccountName && !editingAccount) {
                    return acc.accountName.toLowerCase() !== creatingAccountName.toLowerCase();
                  }
                  return true;
                })
                .map((acc) => ({ 
                  name: acc.accountName, 
                  id: acc.id,
                  isArchived: acc.isArchived 
                }))}
              currentItemId={editingAccount?.id}
              itemType="account"
              errorMessageFormat={(itemName, itemType, item) => {
                const archivedMsg = item?.isArchived ? " (archived)" : "";
                return `An ${itemType} named "${itemName}"${archivedMsg} already exists. Please use a different name.`;
              }}
              onErrorChange={(error) => {
                // Don't update error if we're in the process of saving (to prevent false positives)
                if (!isSavingAccount) {
                  setAccountNameError(error);
                }
              }}
              fullWidth
              placeholder="Enter account name"
            />

            <TextField
              label="Initial Amount"
              type="number"
              inputProps={{ step: "any" }}
              value={formData.amount}
              onChange={(e) =>
                setFormData({ ...formData, amount: e.target.value })
              }
              fullWidth
              placeholder="0.00"
              helperText="Optional - defaults to 0 if not specified"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InfoTooltipIcon
                      tooltipMessage="You can still change the amount manually from here, but it will appear in activity and can't delete later."
                      position="inline"
                      size="small"
                      sx={{ ml: 0.5 }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth required sx={{ position: "relative" }}>
              <InputLabel htmlFor="currency-select">Currency</InputLabel>
              <Select
                id="currency-select"
                value={formData.currency}
                label="Currency"
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value })
                }
                disabled={!!editingAccount}
                sx={{ 
                  "& .MuiSelect-select": {
                    pr: 7, // Add padding to make room for icon
                  }
                }}
              >
                {/* Include default currency (always available) */}
                <MenuItem value={defaultCurrency}>
                  {defaultCurrency}
                </MenuItem>
                {/* Include currencies with rates (not null) */}
                {currencies
                  .filter((c) => c.code !== defaultCurrency && c.rate !== null)
                  .map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </MenuItem>
                  ))}
              </Select>
              <CannotChangeLaterIcon 
                tooltipMessage="Currency cannot be changed after creation. You can define currencies and rates in Settings."
              />
            </FormControl>

            <TextField
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              fullWidth
              placeholder="Add a description..."
            />

            {/* Checkbox to show/hide Account Details */}
            {formData.group && getSelectedGroupDetails().length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAccountDetails}
                    onChange={(e) => {
                      setShowAccountDetails(e.target.checked);
                      // Don't clear details when unchecked - just hide the fields
                      // Details will be preserved until form is submitted or dialog is closed
                    }}
                  />
                }
                label="Fill Account Details"
              />
            )}

            {/* Detail Fields - Show only when checkbox is checked and group has details */}
            {formData.group && getSelectedGroupDetails().length > 0 && showAccountDetails && (
              <Box
                sx={{
                  maxHeight: getSelectedGroupDetails().length >= 3 ? "240px" : "none",
                  overflowY: getSelectedGroupDetails().length >= 3 ? "auto" : "visible",
                  overflowX: "hidden",
                  pr: getSelectedGroupDetails().length >= 3 ? 1 : 0,
                  mt: 1
                }}
              >
                <Stack spacing={2} sx={{ pt: 1.5 }}>
                  {getSelectedGroupDetails().map((detail) => {
                    const currentValue = formData.details?.[detail.name] || "";
                    return (
                      <Box key={detail.name} sx={{ position: "relative" }}>
                        {detail.type === "list" ? (
                          // List type: Show dropdown with list items
                          <FormControl fullWidth>
                            <InputLabel>{detail.name}</InputLabel>
                            <Select
                              value={currentValue || ""}
                              label={detail.name}
                              onChange={(e) => {
                                handleDetailValueChange(detail.name, e.target.value);
                              }}
                            >
                              {detail.listItems && detail.listItems.length > 0 ? (
                                detail.listItems.map((item) => (
                                  <MenuItem key={item} value={item}>
                                    {item}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem value="" disabled>
                                  No items available
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        ) : detail.type === "number" ? (
                          // Number type: Only allow numeric input
                          <TextField
                            label={detail.name}
                            value={currentValue}
                            onKeyDown={(e) => {
                              // Prevent non-numeric characters from being typed
                              const key = e.key;
                              // Allow: numbers, backspace, delete, tab, escape, enter, and special keys (arrows, home, end, etc.)
                              if (
                                key === "Backspace" ||
                                key === "Delete" ||
                                key === "Tab" ||
                                key === "Escape" ||
                                key === "Enter" ||
                                key === "ArrowLeft" ||
                                key === "ArrowRight" ||
                                key === "ArrowUp" ||
                                key === "ArrowDown" ||
                                key === "Home" ||
                                key === "End" ||
                                e.ctrlKey ||
                                e.metaKey ||
                                e.altKey
                              ) {
                                return; // Allow these keys
                              }
                              // Allow decimal point only if not already present
                              if (key === "." || key === ",") {
                                const currentValueStr = String(currentValue || "");
                                if (currentValueStr.includes(".") || currentValueStr.includes(",")) {
                                  e.preventDefault();
                                }
                                return; // Allow one decimal point
                              }
                              // Allow minus sign only at the beginning
                              if (key === "-") {
                                const currentValueStr = String(currentValue || "");
                                if (currentValueStr.length > 0) {
                                  e.preventDefault();
                                }
                                return;
                              }
                              // Only allow numeric characters
                              if (!/^\d$/.test(key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Remove any non-numeric characters except decimal point and minus
                              const cleanedValue = value.replace(/[^\d.-]/g, "");
                              // Ensure minus is only at the start
                              let processedValue = cleanedValue;
                              if (cleanedValue.includes("-") && !cleanedValue.startsWith("-")) {
                                processedValue = cleanedValue.replace(/-/g, "");
                                if (processedValue && !processedValue.startsWith("-")) {
                                  processedValue = "-" + processedValue;
                                }
                              }
                              // Limit to one decimal point
                              const parts = processedValue.split(".");
                              if (parts.length > 2) {
                                processedValue = parts[0] + "." + parts.slice(1).join("");
                              }
                              // Also replace comma with period for decimal
                              processedValue = processedValue.replace(",", ".");
                              
                              if (processedValue === "" || processedValue === "-" || processedValue === ".") {
                                handleDetailValueChange(detail.name, processedValue);
                              } else {
                                const numValue = parseFloat(processedValue);
                                if (!isNaN(numValue) && isFinite(numValue)) {
                                  handleDetailValueChange(detail.name, numValue);
                                } else if (processedValue === "") {
                                  handleDetailValueChange(detail.name, "");
                                }
                              }
                            }}
                            type="text"
                            inputProps={{ 
                              inputMode: "decimal",
                              pattern: "[0-9.-]*"
                            }}
                            fullWidth
                            required={false}
                            disabled={false}
                          />
                        ) : (
                          // String or Text type: Single-line text input (same height as other fields)
                          <TextField
                            label={detail.name}
                            value={currentValue}
                            onChange={(e) => {
                              const value = e.target.value;
                              let processedValue: string = value;
                              
                              // For both string and text types, use single-line (no newlines)
                              // This keeps the field the same height as other fields
                              processedValue = value.replace(/\n/g, "");
                              
                              handleDetailValueChange(detail.name, processedValue);
                            }}
                            fullWidth
                            required={false}
                            disabled={false}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          form="account-form" 
          variant="contained"
          disabled={editingAccount ? !hasFormChanged() : !isAccountFormValid}
          sx={{ flex: 1, py: 1.5 }}
        >
          {editingAccount ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
